import { TeamAbrvToTeamID } from '@shared/constants';
import type { SeasonID } from '@shared/types';
import type { LoaderFunctionArgs } from 'react-router-dom';
import { AppScene } from 'types';
import { AppRoutes } from './Routes';
import type { NavigationTarget } from './navigation.types';

/**
 * Default destination after authentication when no valid protected URL was saved.
 */
export const DEFAULT_AUTH_DESTINATION = AppRoutes.WeeklyGames;

const scenes: Record<string, AppScene> = {
  [AppRoutes.WeeklyGames]: AppScene.WeeklyGames,
  [AppRoutes.Standings]: AppScene.Standings,
  [AppRoutes.CategoryRankings]: AppScene.CategoryRankings,
  [AppRoutes.TeamStats]: AppScene.Teams,
  [AppRoutes.Comparison]: AppScene.Comparison,
  [AppRoutes.ViewTeam]: AppScene.TeamDetails,
  [AppRoutes.Team]: AppScene.TeamDetails,
  [AppRoutes.GameDetails]: AppScene.GameDetails,
  [AppRoutes.HeadToHead]: AppScene.HeadToHead,
};
const invalid: NavigationTarget = { kind: 'not-found' };
const validWeek = (week: string): boolean => /^(?:week(?:[1-9]|1[0-8])|0[1-9]|1[0-8])$/i.test(week);
const validTeam = (team: string): boolean => /^(?:0?[1-9]|[12]\d|3[0-2])$/.test(team);

const validGame = (game: string): boolean => {
  const match = /^(\d{4})(\d{2})(\d{2})_([A-Z]{2,3})@([A-Z]{2,3})$/.exec(game);
  if (!match) return false;
  const [, year, month, day, away, home] = match;
  const date = new Date(Date.UTC(Number(year), Number(month) - 1, Number(day)));
  return (
    date.getUTCFullYear() === Number(year) &&
    date.getUTCMonth() === Number(month) - 1 &&
    date.getUTCDate() === Number(day) &&
    away !== home &&
    Object.hasOwn(TeamAbrvToTeamID, away) &&
    Object.hasOwn(TeamAbrvToTeamID, home)
  );
};

/**
 * Checks an internal URL before rendering a scene or saving a login destination.
 * Query strings and fragments do not affect route matching. When loaded season IDs
 * are supplied, season routes must name one of those seasons; otherwise validation
 * is limited to URL structure so signed-out deep links can be retained.
 *
 * @param {string} destination - Application path, optionally including search and hash.
 * @param {readonly SeasonID[]} availableSeasons - Loaded season IDs, when available.
 *
 * @return {NavigationTarget} Route access level, scene and optional season ID.
 */
export const getNavigationTarget = (destination: string, availableSeasons?: readonly SeasonID[]): NavigationTarget => {
  if (!destination.startsWith('/') || destination.startsWith('//') || /[\\\s]/.test(destination)) return invalid;
  const path = destination.split(/[?#]/)[0].replace(/\/$/, '') || '/';
  if (/%2f|%5c/i.test(path)) return invalid;
  let parts: string[];
  try {
    parts = decodeURIComponent(path).split('/').slice(1);
  } catch {
    return invalid;
  }
  if (path === AppRoutes.Home) return { kind: 'public', scene: AppScene.Home };
  const base = `/${parts[0]}`;
  if (base === AppRoutes.Auth) {
    return parts.length === 1 ||
      (parts.length === 2 && new Set<string>([AppRoutes.Login, AppRoutes.Register, AppRoutes.Forgot]).has(parts[1]))
      ? { kind: 'public', scene: AppScene.Authentication }
      : invalid;
  }
  const scene = scenes[base];
  if (scene === undefined) return invalid;
  if (parts.length === 1) return { kind: 'restricted', scene };
  if (base === AppRoutes.HeadToHead) return invalid;
  if (base === AppRoutes.GameDetails)
    return parts.length === 2 && validGame(parts[1]) ? { kind: 'restricted', scene } : invalid;
  const seasonID = parts[1] as SeasonID;
  if (!/^\d{4}$/.test(seasonID) || (availableSeasons && !availableSeasons.includes(seasonID))) return invalid;
  const valid =
    base === AppRoutes.Comparison
      ? parts.length === 2
      : base === AppRoutes.ViewTeam
        ? parts.length === 3 && validTeam(parts[2])
        : base === AppRoutes.Team
          ? parts.length === 4 && validWeek(parts[2]) && validTeam(parts[3])
          : parts.length === 3 && validWeek(parts[2]);
  return valid ? { kind: 'restricted', scene, seasonID } : invalid;
};

/**
 * Preserves a valid protected destination verbatim, including its query and fragment.
 * Public, malformed and unsupported saved routes use the configured default instead.
 *
 * @param {string} savedDestination - Previously requested application URL.
 * @param {readonly SeasonID[]} availableSeasons - Loaded seasons for optional validation.
 *
 * @return {string} Internal destination to open after authentication.
 */
export const getDestinationAfterAuth = (savedDestination: string, availableSeasons?: readonly SeasonID[]): string =>
  getNavigationTarget(savedDestination, availableSeasons).kind === 'restricted'
    ? savedDestination
    : DEFAULT_AUTH_DESTINATION;

/**
 * Prevents malformed route parameters from reaching loaders that update app state.
 * App renders the not-found scene for the same invalid URL, so no loader payload is needed.
 *
 * @param {(args: LoaderFunctionArgs) => Promise<T>} loader - Existing scene loader.
 * @param {() => readonly SeasonID[] | undefined} getAvailableSeasons - Reads authenticated season IDs at navigation time; undefined keeps structural validation only.
 *
 * @return {(args: LoaderFunctionArgs) => Promise<T | null>} Loader with URL validation.
 */
export const withValidRoute =
  <T>(
    loader: (args: LoaderFunctionArgs) => Promise<T>,
    getAvailableSeasons?: () => readonly SeasonID[] | undefined,
  ): ((args: LoaderFunctionArgs) => Promise<T | null>) =>
  async (args: LoaderFunctionArgs): Promise<T | null> => {
    const url = new URL(args.request.url);
    return getNavigationTarget(url.pathname, getAvailableSeasons?.()).kind === 'not-found' ? null : loader(args);
  };
